import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../config/firebaseConfig";
import { useGestock } from "../GestockContext";
import { showToast } from "../utils";

const MOIS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

// Premier mois avec des données Gestock
const PREMIER_MOIS = { year: 2026, month: 1 };

// Liste des mois, du plus récent (mois en cours) au plus ancien
function listeMois() {
  const now = new Date();
  const out = [];
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  while (y > PREMIER_MOIS.year || (y === PREMIER_MOIS.year && m >= PREMIER_MOIS.month)) {
    out.push({ key: `${y}-${String(m).padStart(2, "0")}`, label: `${MOIS[m - 1]} ${y}`, enCours: out.length === 0 });
    m -= 1;
    if (m === 0) {
      m = 12;
      y -= 1;
    }
  }
  return out;
}

function fmtGenere(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// PDF reçu en base64 → URL locale affichable dans un onglet
function pdfUrl(pdfBase64) {
  const binary = window.atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

const FORMATS = ["rapport", "tableau"];
const FORMAT_LABEL = { rapport: "Rapport", tableau: "Tableau" };
const FORMAT_ICON = { rapport: "📄", tableau: "📊" };

const rapportFn = () => httpsCallable(functions, "gestockMonthlyReport", { timeout: 120000 });

export default function GRapport() {
  const { terminal } = useGestock();
  const [enCours, setEnCours] = useState(null); // `${mois}|${format}` en cours
  const [enregistres, setEnregistres] = useState({}); // `${mois}|${format}` → date

  useEffect(() => {
    let actif = true;
    setEnregistres({});
    rapportFn()({ action: "list", terminal })
      .then(({ data }) => {
        if (!actif) return;
        setEnregistres(
          Object.fromEntries(data.rapports.map((r) => [`${r.month}|${r.format}`, r.generatedAt])),
        );
      })
      .catch((err) => console.error("gestockMonthlyReport list:", err));
    return () => {
      actif = false;
    };
  }, [terminal]);

  const noter = (mois, format, generatedAt) =>
    generatedAt &&
    setEnregistres((prev) => ({ ...prev, [`${mois.key}|${format}`]: generatedAt }));

  async function ouvrir(mois, format) {
    if (enCours) return;
    setEnCours(`${mois.key}|${format}`);
    // L'onglet est ouvert pendant le clic : ouvert après l'attente de la
    // génération, il serait bloqué par le navigateur (Safari iPhone surtout).
    const onglet = window.open("", "_blank");
    if (onglet) {
      onglet.document.title = `${FORMAT_LABEL[format]} ${mois.label} — ${terminal}`;
      onglet.document.body.innerHTML =
        '<p style="font-family:sans-serif;padding:24px;color:#555">⏳ Chargement…</p>';
    }
    try {
      const { data } = await rapportFn()({ terminal, month: mois.key, format });
      const url = pdfUrl(data.pdfBase64);
      if (onglet && !onglet.closed) {
        onglet.location.href = url;
      } else {
        // Onglet bloqué : ouverture dans la page courante
        window.location.assign(url);
      }
      // Laisse le temps à l'onglet de charger le PDF avant de libérer l'URL
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
      noter(mois, format, data.generatedAt);
    } catch (err) {
      console.error("gestockMonthlyReport:", err);
      if (onglet && !onglet.closed) onglet.close();
      showToast("Ouverture du rapport impossible", "error");
    } finally {
      setEnCours(null);
    }
  }

  // Régénère les deux versions d'un mois passé (après une correction)
  async function regenerer(mois) {
    if (enCours) return;
    setEnCours(`${mois.key}|tous`);
    try {
      for (const format of FORMATS) {
        const { data } = await rapportFn()({ terminal, month: mois.key, format, regenerate: true });
        noter(mois, format, data.generatedAt);
      }
      showToast(`${mois.label} régénéré ✔`);
    } catch (err) {
      console.error("gestockMonthlyReport regenerate:", err);
      showToast("Régénération impossible", "error");
    } finally {
      setEnCours(null);
    }
  }

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-title">
          Rapports <span>mensuels</span>
        </div>
        <div className="page-sub">
          {"// "}TERMINAL {terminal} — RAPPORT DÉTAILLÉ OU TABLEAU JOURNALIER
        </div>
      </div>

      <div className="card">
        {listeMois().map((mois) => {
          const dates = FORMATS.map((f) => enregistres[`${mois.key}|${f}`]).filter(Boolean);
          const genere = dates.length ? fmtGenere(dates.sort().at(-1)) : null;
          return (
            <div
              key={mois.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
                padding: "12px 14px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{mois.label}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                  {mois.enCours
                    ? "Mois en cours · mis à jour à l'ouverture"
                    : genere
                      ? `Enregistré le ${genere}`
                      : "Pas encore généré"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {!mois.enCours && genere && (
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Régénérer le rapport et le tableau"
                    disabled={!!enCours}
                    onClick={() => regenerer(mois)}
                  >
                    {enCours === `${mois.key}|tous` ? "⏳" : "↻"}
                  </button>
                )}
                {FORMATS.map((format) => (
                  <button
                    key={format}
                    className={`btn btn-sm ${format === "rapport" ? "btn-blue" : "btn-green"}`}
                    disabled={!!enCours}
                    onClick={() => ouvrir(mois, format)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {enCours === `${mois.key}|${format}`
                      ? "⏳ …"
                      : `${FORMAT_ICON[format]} ${FORMAT_LABEL[format]}`}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
